import { Activity, CheckCircle2, Flag, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import AdminStatsCard from "../components/admin/AdminStatsCard";
import ClaimsManagementPanel from "../components/admin/ClaimsManagementPanel";
import FlaggedIssuesPanel from "../components/admin/FlaggedIssuesPanel";
import api from "../services/api";

const AdminDashboard = () => {
  const [metrics, setMetrics] = useState({
    totalIssues: 0,
    resolvedIssues: 0,
    pendingIssues: 0,
    flaggedIssues: 0,
    totalUsers: 0,
    verifiedUsers: 0,
    pendingClaims: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const res = await api.get("/admin/dashboard-metrics");
        setMetrics((current) => ({ ...current, ...(res.data || {}) }));
      } catch (err) {
        console.error("Error fetching admin metrics:", err);
        toast.error("Failed to load dashboard metrics");
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center rounded-[2rem] border border-[#d7dfd4] bg-white py-24 shadow-sm">
        <div className="text-center">
          <div className="mx-auto mb-4 h-14 w-14 animate-spin rounded-full border-b-4 border-[#ff9933]" />
          <p className="text-slate-600">Loading dashboard metrics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <section className="relative overflow-hidden rounded-[2rem] border border-[#d7dfd4] bg-white shadow-sm">
        <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-[#ff9933] via-white to-[#138808]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(255,153,51,0.16),_transparent_28%),radial-gradient(circle_at_bottom_right,_rgba(19,136,8,0.12),_transparent_30%)]" />
        <div className="relative grid gap-8 px-6 py-8 lg:grid-cols-[1.3fr_0.9fr] lg:px-8">
          <div className="space-y-4">
            <span className="inline-flex rounded-full border border-[#ffddb3] bg-[#fff1df] px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-[#b76500]">
              Admin Command Center
            </span>
            <h2 className="max-w-3xl text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
              Clean oversight for claims, flagged content, and issue resolution across JanShakti.
            </h2>
            <p className="max-w-2xl text-sm leading-7 text-slate-600 sm:text-base">
              This workspace is tuned for admin review only. Each section is live, backed by the API, and designed around the saffron-white-green visual system used across the frontend.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-3xl border border-[#ffe1b8] bg-[#fff8ef] p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#b76500]">Pending workload</p>
              <p className="mt-3 text-3xl font-semibold text-slate-900">{metrics.pendingIssues.toLocaleString()}</p>
              <p className="mt-2 text-sm text-slate-500">Open issue reviews waiting for admin movement.</p>
            </div>
            <div className="rounded-3xl border border-[#d3ead6] bg-[#f3fbf4] p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#106d06]">Claims queue</p>
              <p className="mt-3 text-3xl font-semibold text-slate-900">{metrics.pendingClaims.toLocaleString()}</p>
              <p className="mt-2 text-sm text-slate-500">Candidate verification requests still awaiting review.</p>
            </div>
            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5 sm:col-span-2">
              <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
                <span className="inline-flex items-center gap-2"><Activity className="h-4 w-4 text-[#ff9933]" /> Live moderation</span>
                <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#138808]" /> Verification actions</span>
                <span className="inline-flex items-center gap-2"><Flag className="h-4 w-4 text-[#d95d39]" /> Escalation handling</span>
                <span className="inline-flex items-center gap-2"><Users className="h-4 w-4 text-[#2f6fed]" /> Trust and users</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
        <AdminStatsCard icon="Issues" title="Total Issues" value={metrics.totalIssues} color="orange" delay={0} caption="All issue records in the system" />
        <AdminStatsCard icon="Resolved" title="Resolved Issues" value={metrics.resolvedIssues} color="green" delay={0.08} caption="Successfully closed issues" />
        <AdminStatsCard icon="Flagged" title="Flagged Issues" value={metrics.flaggedIssues} color="red" delay={0.16} caption="Items needing moderation" />
        <AdminStatsCard icon="Verified" title="Verified Users" value={metrics.verifiedUsers} color="blue" delay={0.24} caption="Trusted verified user accounts" />
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        <motion.section
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.3 }}
          className="overflow-hidden rounded-[2rem] border border-[#d7dfd4] bg-white shadow-sm"
        >
          <div className="border-b border-slate-100 px-6 py-5">
            <h3 className="text-lg font-semibold text-slate-900">Flagged content queue</h3>
            <p className="mt-1 text-sm text-slate-500">The most recent moderation cases from automated review.</p>
          </div>
          <div className="p-6">
            <FlaggedIssuesPanel compact />
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.3 }}
          className="overflow-hidden rounded-[2rem] border border-[#d7dfd4] bg-white shadow-sm"
        >
          <div className="border-b border-slate-100 px-6 py-5">
            <h3 className="text-lg font-semibold text-slate-900">Claims awaiting decision</h3>
            <p className="mt-1 text-sm text-slate-500">A live view into the current candidate verification pipeline.</p>
          </div>
          <div className="p-6">
            <ClaimsManagementPanel compact />
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default AdminDashboard;
